import { useEffect, useState } from 'react'
import { TRelatedProduct } from '@cannect/services/resources/progressive-discounts'
import { formatReal } from '@cannect/utils/formatters'

const GREEN = '#748351'

interface ProgressiveGroupOfferProps {
  products: TRelatedProduct[]
  mode: 'full' | 'sheet'
  onAdd: (productId: number) => void | Promise<void>
  /** Desconto ativo do grupo (%) — aplicado ao price do produto para mostrar o preço que o usuário pagaria. */
  groupDiscountPct?: number
  /** Inicia expandido. Default: false. */
  defaultOpen?: boolean
  /**
   * Sinal de abertura externa: cada vez que esse número muda (> 0), a seção abre.
   * Útil para o pai abrir a oferta ao alterar quantidade de um item do grupo.
   */
  openSignal?: number
  /** Chamado quando o estado interno de aberto/fechado muda. */
  onToggle?: (isOpen: boolean) => void
}

const SmallSpinner = () => (
  <svg className="animate-spin" width={12} height={12} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity={0.25} />
    <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
)

function MiniCard({
  product,
  mode,
  onAdd,
  isLoading,
  groupDiscountPct
}: {
  product: TRelatedProduct
  mode: 'full' | 'sheet'
  onAdd: (productId: number) => void | Promise<void>
  isLoading?: boolean
  groupDiscountPct?: number
}) {
  const imgSize = mode === 'sheet' ? 48 : 64
  const label = product.translated_name || product.name
  const basePrice = Number(product.price) || 0
  const pct = Number(groupDiscountPct) || 0
  // Preço que o usuário pagaria ao adicionar este produto ao grupo com desconto ativo
  const finalPrice = pct > 0 ? +(basePrice * (1 - pct / 100)).toFixed(2) : basePrice
  const hasDiscount = pct > 0 && basePrice > 0

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: mode === 'sheet' ? 'row' : 'column',
        alignItems: mode === 'sheet' ? 'center' : 'flex-start',
        gap: mode === 'sheet' ? 10 : 6,
        width: mode === 'full' ? 148 : '100%',
        border: '1px solid #ecece2',
        borderRadius: 8,
        padding: 8,
        background: '#fff',
        flexShrink: 0
      }}>
      {product.image ? (
        <img
          src={product.image}
          alt={label}
          style={{ width: imgSize, height: imgSize, objectFit: 'contain', flexShrink: 0, borderRadius: 4 }}
        />
      ) : (
        <div
          style={{
            width: imgSize,
            height: imgSize,
            background: '#edece7',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: '.1em',
            color: GREEN,
            flexShrink: 0
          }}>
          CANNECT
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: '#3f3f3f',
            lineHeight: 1.35,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' as const
          }}>
          {label}
        </span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap' }}>
          {hasDiscount && (
            <span style={{ fontSize: 10, color: '#aaa', textDecoration: 'line-through' }}>
              {formatReal(basePrice) as string}
            </span>
          )}
          <span style={{ fontSize: 12, fontWeight: 700, color: hasDiscount ? '#748351' : '#3f3f3f' }}>
            {formatReal(finalPrice) as string}
          </span>
        </span>
      </div>

      <button
        type="button"
        disabled={isLoading}
        onClick={() => onAdd(product.id)}
        style={{
          marginTop: mode === 'full' ? 'auto' : 0,
          padding: '5px 10px',
          fontSize: 11,
          fontWeight: 600,
          color: GREEN,
          border: `1px solid ${GREEN}`,
          borderRadius: 6,
          background: 'transparent',
          cursor: isLoading ? 'default' : 'pointer',
          opacity: isLoading ? 0.7 : 1,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4
        }}>
        {isLoading ? <SmallSpinner /> : '+ Adicionar'}
      </button>
    </div>
  )
}

/** Seção de oferta cruzada dentro do card de grupo: produtos da mesma regra ainda fora do carrinho. */
export function ProgressiveGroupOffer({
  products,
  mode,
  onAdd,
  groupDiscountPct,
  defaultOpen = false,
  openSignal,
  onToggle
}: ProgressiveGroupOfferProps) {
  const [open, setOpen] = useState(defaultOpen)
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set())

  // Abre quando o sinal externo muda — ignora montagem (openSignal=0) e só abre quando > 0
  useEffect(() => {
    if (!openSignal) return
    setOpen(true)
  }, [openSignal])

  if (!products.length) return null

  const handleToggle = () => {
    const next = !open
    setOpen(next)
    onToggle?.(next)
  }

  const handleAdd = async (productId: number) => {
    if (loadingIds.has(productId)) return
    setLoadingIds((prev) => new Set([...prev, productId]))
    try {
      await onAdd(productId)
    } finally {
      setLoadingIds((prev) => {
        const s = new Set(prev)
        s.delete(productId)
        return s
      })
    }
  }

  return (
    <div style={{ borderTop: '1px solid #e9edd5' }}>
      {/* Header clicável — sempre visível */}
      <button
        type="button"
        onClick={handleToggle}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left'
        }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: GREEN }}>Complete o grupo — adicione e ganhe mais</span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            fontWeight: 600,
            color: GREEN
          }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#e9edd5',
              color: GREEN,
              borderRadius: 999,
              padding: '1px 7px',
              fontSize: 10.5,
              fontWeight: 700
            }}>
            {products.length} produto{products.length !== 1 ? 's' : ''}
          </span>
          <span
            style={{
              display: 'inline-block',
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              fontSize: 10
            }}>
            ▼
          </span>
        </span>
      </button>

      {/* Conteúdo colapsável — animado via max-height */}
      <div
        style={{
          overflow: 'hidden',
          maxHeight: open ? 600 : 0,
          opacity: open ? 1 : 0,
          transition: 'max-height 0.28s ease, opacity 0.2s ease'
        }}>
        <div
          style={{
            padding: '0 12px 10px',
            display: 'flex',
            flexDirection: mode === 'sheet' ? 'column' : 'row',
            flexWrap: mode === 'full' ? 'wrap' : undefined,
            gap: 8,
            overflowX: mode === 'sheet' ? 'hidden' : 'visible'
          }}>
          {products.map((p) => (
            <MiniCard
              key={p.id}
              product={p}
              mode={mode}
              onAdd={handleAdd}
              isLoading={loadingIds.has(p.id)}
              groupDiscountPct={groupDiscountPct}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
